import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { isForbiddenConversationAccessError } from '@/lib/canvas/canvas-conversation-hub-sync';
import { aiConversationApi } from '@/services/api';
import { PAGINATION } from '@/constants/pagination';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { useNotifyAIJobSubmit } from './useAIJobs';
import type { PaginatedResponse } from '@/types/api';
import type {
  ConversationContext,
  ConversationCreateRequest,
  ConversationDetail,
  ConversationMessage,
  ConversationMessageListParams,
  ConversationMessagesBatchItem,
  ConversationMediaTypeValue,
  ConversationSendMessageRequest,
  ConversationStatusValue,
  ConversationSummary,
  ConversationTargetTypeValue,
  ConversationTriggerGenerateRequest,
} from '@/types/ai-conversation';

const CONVERSATION_QUERY_KEY = 'ai-conversations' as const;
const RESOLVE_CONVERSATION_STALE_TIME = 20_000;
const PERSONAL_CONTEXT_QUERY_KEY = '__personal__';

function resolveConversationScope(activeOrgId: string | null): string {
  return activeOrgId ?? PERSONAL_CONTEXT_QUERY_KEY;
}

export const aiConversationKeys = {
  all: [CONVERSATION_QUERY_KEY] as const,
  lists: () => [...aiConversationKeys.all, 'list'] as const,
  list: (activeOrgId: string | null, params: Record<string, unknown>) =>
    [...aiConversationKeys.lists(), resolveConversationScope(activeOrgId), params] as const,
  messagesRoot: (conversationId: string) =>
    [...aiConversationKeys.all, 'messages', conversationId] as const,
  messages: (
    activeOrgId: string | null,
    conversationId: string,
    page: number,
    pageSize: number,
    order: 'asc' | 'desc'
  ) =>
    [
      ...aiConversationKeys.messagesRoot(conversationId),
      resolveConversationScope(activeOrgId),
      'page',
      page,
      pageSize,
      order,
    ] as const,
  messagesInfinite: (
    activeOrgId: string | null,
    conversationId: string,
    pageSize: number,
    order: 'asc' | 'desc'
  ) =>
    [
      ...aiConversationKeys.messagesRoot(conversationId),
      resolveConversationScope(activeOrgId),
      'infinite',
      pageSize,
      order,
    ] as const,
  messagesBatch: (
    activeOrgId: string | null,
    conversationIds: readonly string[],
    pageSize: number,
    order: 'asc' | 'desc'
  ) =>
    [
      ...aiConversationKeys.all,
      'messages-batch',
      resolveConversationScope(activeOrgId),
      conversationIds,
      pageSize,
      order,
    ] as const,
};

export function seedConversationMessagesInfiniteQueries(
  queryClient: QueryClient,
  params: {
    activeOrgId: string | null;
    pageSize: number;
    order: 'asc' | 'desc';
    items: readonly ConversationMessagesBatchItem[];
  }
): void {
  for (const item of params.items) {
    const { conversationId, ...page } = item;
    queryClient.setQueryData<InfiniteData<PaginatedResponse<ConversationMessage>, number>>(
      aiConversationKeys.messagesInfinite(
        params.activeOrgId,
        conversationId,
        params.pageSize,
        params.order
      ),
      {
        pages: [page],
        pageParams: [page.page],
      }
    );
  }
}

function buildResolveConversationListParams(context: ConversationContext | null) {
  return context
    ? {
        projectId: context.projectId,
        targetType: context.targetType,
        targetId: context.targetId,
        tagId: context.tagId,
        mediaType: context.mediaType,
        page: 1,
        pageSize: 50,
      }
    : {};
}

function selectResolvedConversation(
  context: ConversationContext,
  data: PaginatedResponse<ConversationSummary>
): ConversationSummary | null {
  if (!data?.items?.length) return null;

  const sorted = data.items
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (context.tagId) {
    return sorted.find((item) => item.tagId === context.tagId) ?? null;
  }

  return sorted.find((item) => item.tagId == null) ?? null;
}

export function buildConversationContextKey(context: ConversationContext | null) {
  if (!context) return '';
  return [
    context.projectId ?? 'none',
    context.targetType,
    context.targetId,
    context.tagId ?? 'none',
    context.mediaType,
  ].join(':');
}

export function canCreateConversationForContext(
  context: ConversationContext | null
): context is ConversationContext {
  if (!context) return false;

  const requiresTag =
    context.targetType === 2 || context.targetType === 3 || context.targetType === 4;

  return !requiresTag || Boolean(context.tagId);
}

export function useConversationList(
  params: {
    projectId?: string;
    targetType?: ConversationTargetTypeValue;
    targetId?: string;
    tagId?: string | null;
    mediaType?: ConversationMediaTypeValue;
    status?: ConversationStatusValue;
    page?: number;
    pageSize?: number;
  },
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery({
    queryKey: aiConversationKeys.list(activeOrgId, params),
    queryFn: () => aiConversationApi.listConversations(params, { activeOrgId }),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useResolveConversation(
  context: ConversationContext | null,
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const listParams = buildResolveConversationListParams(context);

  return useQuery({
    queryKey: aiConversationKeys.list(activeOrgId, listParams),
    queryFn: () => aiConversationApi.listConversations(listParams, { activeOrgId }),
    enabled: Boolean(context) && (options?.enabled ?? true),
    staleTime: RESOLVE_CONVERSATION_STALE_TIME,
    select: (data: PaginatedResponse<ConversationSummary>): ConversationSummary | null =>
      context ? selectResolvedConversation(context, data) : null,
  });
}

export function useCreateConversation() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useMutation({
    mutationFn: (payload: ConversationCreateRequest) =>
      aiConversationApi.createConversation(payload, { activeOrgId }),
  });
}

export function useEnsureConversationForSubmit() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();
  const createConversation = useCreateConversation();

  return useCallback(
    async (params: {
      conversationId?: string | null;
      resolvedConversation?: ConversationSummary | null;
      context: ConversationContext | null;
    }) => {
      if (params.conversationId) return params.conversationId;
      if (params.resolvedConversation?.conversationId) {
        return params.resolvedConversation.conversationId;
      }
      if (!canCreateConversationForContext(params.context)) return null;

      const listParams = buildResolveConversationListParams(params.context);
      const listData = await queryClient.fetchQuery({
        queryKey: aiConversationKeys.list(activeOrgId, listParams),
        queryFn: () => aiConversationApi.listConversations(listParams, { activeOrgId }),
        staleTime: RESOLVE_CONVERSATION_STALE_TIME,
      });
      const resolvedConversation = selectResolvedConversation(params.context, listData);
      if (resolvedConversation?.conversationId) {
        return resolvedConversation.conversationId;
      }

      const created = await createConversation.mutateAsync(params.context);
      return created.conversationId;
    },
    [activeOrgId, createConversation, queryClient]
  );
}

export function useArchiveConversation() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      aiConversationApi.updateConversation(conversationId, { status: 2 }, { activeOrgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiConversationKeys.lists() });
    },
  });
}

export function useDeleteConversation() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      aiConversationApi.deleteConversation(conversationId, { activeOrgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiConversationKeys.lists() });
    },
  });
}

export function useSendConversationMessage() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();
  const notifyAIJobSubmit = useNotifyAIJobSubmit();

  return useMutation({
    mutationFn: ({
      conversationId,
      payload,
    }: {
      conversationId: string;
      payload: ConversationSendMessageRequest;
    }) => aiConversationApi.sendMessage(conversationId, payload, { activeOrgId }),
    onSuccess: (data, variables) => {
      notifyAIJobSubmit(data.generation?.jobId);
      queryClient.invalidateQueries({
        queryKey: aiConversationKeys.messagesRoot(variables.conversationId),
      });
    },
  });
}

export function useWithdrawConversationMessage() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { conversationId: string; messageId: string }) =>
      aiConversationApi.withdrawMessage(payload, { activeOrgId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: aiConversationKeys.messagesRoot(variables.conversationId),
      });
    },
  });
}

export function useConversationMessages(
  conversationId: string | null,
  params?: ConversationMessageListParams,
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? PAGINATION.aiConversation.messagesPageSize;
  const order = params?.order ?? 'asc';

  return useQuery({
    queryKey: conversationId
      ? aiConversationKeys.messages(activeOrgId, conversationId, page, pageSize, order)
      : [
          ...aiConversationKeys.messagesRoot('empty'),
          resolveConversationScope(activeOrgId),
          'page',
          page,
          pageSize,
          order,
        ],
    queryFn: () =>
      aiConversationApi.listMessages(conversationId!, { page, pageSize, order }, { activeOrgId }),
    enabled: Boolean(conversationId) && (options?.enabled ?? true),
    staleTime: 2_000,
  });
}

export function useInfiniteConversationMessages(
  conversationId: string | null,
  params?: ConversationMessageListParams,
  options?: { enabled?: boolean; staleTime?: number }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const pageSize = params?.pageSize ?? PAGINATION.aiConversation.messagesPageSize;
  const order = params?.order ?? 'asc';
  const enabled = Boolean(conversationId) && (options?.enabled ?? true);

  return useInfiniteQuery<
    PaginatedResponse<ConversationMessage>,
    Error,
    InfiniteData<PaginatedResponse<ConversationMessage>, number>,
    ReturnType<typeof aiConversationKeys.messagesInfinite>,
    number
  >({
    queryKey: conversationId
      ? aiConversationKeys.messagesInfinite(activeOrgId, conversationId, pageSize, order)
      : [
          ...aiConversationKeys.messagesRoot('empty'),
          resolveConversationScope(activeOrgId),
          'infinite',
          pageSize,
          order,
        ],
    queryFn: ({ pageParam }) =>
      aiConversationApi.listMessages(
        conversationId!,
        { page: pageParam, pageSize, order },
        { activeOrgId }
      ),
    enabled,
    retry: (failureCount, error) =>
      isForbiddenConversationAccessError(error) ? false : failureCount < 2,
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: options?.staleTime ?? 2_000,
  });
}

export function useConversationHasContent(
  conversationId: string | null,
  options?: { enabled?: boolean }
): { hasContent: boolean; isLoading: boolean } {
  const query = useInfiniteConversationMessages(
    conversationId,
    {
      pageSize: PAGINATION.aiConversation.messagesPageSize,
      order: 'desc',
    },
    {
      enabled: options?.enabled ?? true,
    }
  );

  return {
    hasContent: (query.data?.pages[0]?.total ?? 0) > 0,
    isLoading: query.isLoading,
  };
}

const CANVAS_MESSAGES_BATCH_STALE_TIME = 15_000;

export function useCanvasConversationMessagesBatch(conversationIds: readonly string[]) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();
  const pageSize = PAGINATION.aiConversation.messagesPageSize;
  const order = 'desc' as const;
  const ids = useMemo(
    () => Array.from(new Set(conversationIds.filter((id) => id.trim().length > 0))).sort(),
    [conversationIds]
  );

  return useQuery({
    queryKey: aiConversationKeys.messagesBatch(activeOrgId, ids, pageSize, order),
    queryFn: async () => {
      const result = await aiConversationApi.listMessagesBatch(
        {
          conversationIds: ids,
          page: 1,
          pageSize,
          order,
        },
        { activeOrgId }
      );
      seedConversationMessagesInfiniteQueries(queryClient, {
        activeOrgId,
        pageSize,
        order,
        items: result.items,
      });
      return result;
    },
    enabled: ids.length > 0,
    staleTime: CANVAS_MESSAGES_BATCH_STALE_TIME,
  });
}

export function useTriggerConversationGenerate() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const notifyAIJobSubmit = useNotifyAIJobSubmit();

  return useMutation({
    mutationFn: ({
      conversationId,
      payload,
    }: {
      conversationId: string;
      payload: ConversationTriggerGenerateRequest;
    }) => aiConversationApi.triggerGenerate(conversationId, payload, { activeOrgId }),
    onSuccess: (data) => notifyAIJobSubmit(data.jobId),
  });
}

export function useSubmitMidjourneySeparateAction() {
  const queryClient = useQueryClient();
  const notifyAIJobSubmit = useNotifyAIJobSubmit();

  return useMutation({
    mutationFn: ({
      conversationId,
      messageId,
      index,
    }: {
      conversationId: string;
      messageId: string;
      index: number;
    }) => aiConversationApi.submitMidjourneySeparateAction(conversationId, messageId, index),
    onSuccess: (data, variables) => {
      notifyAIJobSubmit(data.jobId);
      queryClient.invalidateQueries({
        queryKey: [...aiConversationKeys.all, 'messages', variables.conversationId],
      });
    },
  });
}

// ========================================================================
// 缓存操作工具函数（供其他 hooks 调用，避免直接 import aiConversationKeys）
// ========================================================================

/**
 * 失活指定会话的消息列表缓存。
 * 在生成 prompt 成功后调用，确保消息列表显示最新内容。
 */
export function invalidateAIConversationMessages(
  queryClient: QueryClient,
  conversationId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: aiConversationKeys.messagesRoot(conversationId),
  });
}

export function invalidateAIConversationLists(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    queryKey: aiConversationKeys.lists(),
  });
}

export function insertResolvedConversationListItem(
  queryClient: QueryClient,
  params: {
    activeOrgId: string | null;
    context: ConversationContext;
    conversation: ConversationDetail;
  }
): void {
  const listParams = buildResolveConversationListParams(params.context);
  queryClient.setQueryData<PaginatedResponse<ConversationSummary>>(
    aiConversationKeys.list(params.activeOrgId, listParams),
    (current) => {
      const nextItem: ConversationSummary = params.conversation;
      if (!current) {
        return {
          items: [nextItem],
          total: 1,
          page: 1,
          pageSize: 50,
          totalPages: 1,
        };
      }
      const without = current.items.filter(
        (item) => item.conversationId !== nextItem.conversationId
      );
      const insertedNew = without.length === current.items.length;
      return {
        ...current,
        items: [nextItem, ...without],
        total: insertedNew ? current.total + 1 : current.total,
      };
    }
  );
}

export function useInvalidateAIConversationLists() {
  const queryClient = useQueryClient();
  return useCallback(() => invalidateAIConversationLists(queryClient), [queryClient]);
}

/**
 * 返回一个函数，用于主动预取指定对话的消息列表（第一页）。
 * 在对话 ID 确定后立即调用，可减少用户感知到的加载延迟。
 */
export function useAIConversationPrefetch() {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const queryClient = useQueryClient();
  return useCallback(
    (conversationId: string, pageSize: number) =>
      queryClient.prefetchInfiniteQuery({
        queryKey: aiConversationKeys.messagesInfinite(
          activeOrgId,
          conversationId,
          pageSize,
          'desc'
        ),
        queryFn: ({ pageParam }) =>
          aiConversationApi.listMessages(
            conversationId,
            {
              page: pageParam,
              pageSize,
              order: 'desc',
            },
            { activeOrgId }
          ),
        initialPageParam: 1,
        getNextPageParam: (lastPage: PaginatedResponse<ConversationMessage>) =>
          lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
        staleTime: 2_000,
      }),
    [activeOrgId, queryClient]
  );
}
